import { useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { ArrowUpRight, ArrowDownRight, Trophy, AlertTriangle, Layers, Download } from "lucide-react";
import { toast } from "sonner";
import api, { API, TOKEN_KEY } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { rp, num, pcs, pct, monthOptions, signed } from "@/lib/format";
import { PageTitle, Panel, Stat } from "@/components/common";
import AiSummary from "@/components/AiSummary";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function Mom({ label, value, unit }) {
  if (value == null) return <div className="text-xs text-slate-400">{label}: data bulan lalu belum ada</div>;
  const up = value >= 0;
  return (
    <div className="flex items-center gap-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={`inline-flex items-center font-semibold ${up ? "text-emerald-700" : "text-rose-700"}`}>
        {up ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
        {up ? "+" : ""}{value}{unit}
      </span>
    </div>
  );
}

export default function Analytics() {
  const [month, setMonth] = useState(monthOptions(1)[0].value);
  const [data, setData] = useState(null);
  const [lb, setLb] = useState(null);
  const [graph, setGraph] = useState([]);
  const months = monthOptions(12);

  useEffect(() => {
    api.get(`/analytics/monthly?month=${month}`).then((r) => setData(r.data));
    api.get(`/analytics/leaderboard?month=${month}`).then((r) => setLb(r.data));
    api.get(`/dashboard/daily-graph?month=${month}`).then((r) => setGraph(r.data.series));
  }, [month]);

  if (!data) return <div className="text-slate-500">Memuat analitik…</div>;
  const c = data.current;
  const doExport = (fmt) => {
    fetch(`${API}/export/analytics?month=${month}&fmt=${fmt}`, { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => { const url = URL.createObjectURL(b); const a = document.createElement("a"); a.href = url; a.download = `analisis_${month}.${fmt}`; a.click(); URL.revokeObjectURL(url); toast.success(`EKSPOR ${fmt.toUpperCase()} BERHASIL`); })
      .catch(() => toast.error("EKSPOR GAGAL"));
  };

  return (
    <div>
      <PageTitle title="Analisis Produksi" desc={`Ringkasan & perbandingan bulanan — ${data.month_label}`}
        right={<div className="flex gap-2 items-center">
          <Button variant="outline" onClick={() => doExport("xlsx")} data-testid="analytics-export-xlsx"><Download className="w-4 h-4 mr-1.5" />Excel</Button>
          <Button variant="outline" onClick={() => doExport("pdf")} data-testid="analytics-export-pdf"><Download className="w-4 h-4 mr-1.5" />PDF</Button>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-48" data-testid="analytics-month"><SelectValue /></SelectTrigger>
            <SelectContent>{months.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select></div>
        } />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
        <Panel title="Ringkasan Bulanan" className="lg:col-span-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="Total Keluar" value={pcs(c.total_ambil)} tone="blue" />
            <Stat label="Total Setor" value={pcs(c.total_setor)} tone="emerald" />
            <Stat label="Total Good" value={pcs(c.total_good)} tone="emerald" />
            <Stat label="Total Reject" value={pcs(c.total_reject)} sub={pct(c.reject_rate)} tone="rose" />
            <Stat label="Open WIP" value={pcs(data.current_open_wip)} tone="amber" sub={data.wip_rekonsiliasi?.perlu ? `PERLU REKONSILIASI · SETOR MELEBIHI AMBIL ${pcs(data.wip_rekonsiliasi.selisih)}` : undefined} />
            <Stat label="Total Bon" value={rp(c.total_bon)} tone="indigo" />
            <Stat label="Total Ongkos" value={rp(c.gross_sewing_pay)} />
            <Stat label="Outstanding" value={rp(c.outstanding_payable)} tone="rose" />
          </div>
        </Panel>
        <Panel title="Month-over-Month" className="lg:col-span-4">
          {data.has_previous ? (
            <div className="space-y-3">
              <div className="text-xs text-slate-400">vs {data.previous_label}</div>
              <Mom label="Setor" value={data.mom.setor_pct} unit="%" />
              <Mom label="Good Output" value={data.mom.good_pct} unit="%" />
              <Mom label="Reject Rate" value={data.mom.reject_rate_pp} unit=" pp" />
              <Mom label="Bon" value={data.mom.bon_pct} unit="%" />
            </div>
          ) : <div className="text-sm text-slate-400">Belum ada data bulan sebelumnya untuk dibandingkan.</div>}
        </Panel>
      </div>

      <div className="mb-6"><AiSummary month={month} /></div>

      <Panel title="Grafik Produksi Bulanan" className="mb-6">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={graph} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }} formatter={(v, n) => [v == null ? "-" : num(v), n]} labelFormatter={(l) => `Tanggal ${l}`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="ambil" name="Ambil" fill="#2563EB" radius={[2, 2, 0, 0]} />
              <Bar dataKey="setor" name="Setor" fill="#059669" radius={[2, 2, 0, 0]} />
              <Bar dataKey="reject" name="Reject" fill="#E11D48" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      {lb && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <LbCard title="Top Output" icon={Trophy} rows={lb.top_output} render={(w) => pcs(w.good)} />
          <LbCard title="Highest Reject Rate" icon={AlertTriangle} rows={lb.highest_reject} render={(w) => pct(w.reject_rate)} tone="rose" />
          <LbCard title="Largest Open WIP" icon={Layers} rows={lb.largest_open_wip} render={(w) => pcs(w.open_wip)} tone="amber" />
        </div>
      )}
    </div>
  );
}

function LbCard({ title, icon: Icon, rows, render, tone = "slate" }) {
  return (
    <Panel title={title}>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-sm text-slate-400">Tidak ada data.</div>}
        {rows.map((w, i) => (
          <div key={w.worker_id} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-5 h-5 rounded bg-slate-100 text-slate-500 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
              <span className="font-semibold text-slate-800">{w.worker_id}</span>
              <span className="text-slate-500 truncate">{w.worker_name}</span>
            </div>
            <span className={`mono font-bold ${tone === "rose" ? "text-rose-700" : tone === "amber" ? "text-amber-700" : "text-slate-900"}`}>{render(w)}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}
